import { formatPrice } from '@/utils/circuits/prices/formatPrice';
import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { EstimateStatus } from '../estimates/EstimateStatus';
import EstimateStatusCell from '@/components/estimates/EstimateStatusCell';
import { formatCurrency } from '@/utils/prices/formatCurrency';
import { Translator } from '../translator.type';

export type IncomingReservation = {
  id: string;
  ref: number;
  arrivalDate: Date;
  status: EstimateStatus;
  amount: number | null;
  project: {
    client: {
      id: string;
      ref: number;
      mainContact: {
        firstName: string;
        lastName: string;
      };
    };
  };
  reservationAgent: {
    firstname: string;
    lastname: string;
    agency: {
      id: string;
      name: string;
    };
  };
  circuit: {
    code: number;
    name: string;
    countries: string[];
    currencies: string[];
    durationDays: number;
    durationNights: number;
  };
  reservationRequestSentAt: string | null;
  confirmedBy?: {
    firstname: string;
    lastname: string;
  };
};

export const RESERVATION_COLUMNS = (
  t: Translator,
): ColumnDef<IncomingReservation>[] => [
  {
    accessorKey: 'ref',
    header: t('reservations.columns.estimate'),
    cell: ({ row }) => (
      <span>{`D${row.original.ref.toString().padStart(4, '0')}`}</span>
    ),
  },
  {
    accessorKey: 'project.client.ref',
    header: t('reservations.columns.client'),
    cell: ({ row }) => (
      <span>{`C${row.original.project.client.ref.toString().padStart(4, '0')}`}</span>
    ),
  },
  {
    accessorKey: 'project.client.mainContact.firstName',
    header: t('reservations.columns.clientName'),
    cell: ({ row }) => (
      <span>{`${row.original.project.client.mainContact.firstName} ${row.original.project.client.mainContact.lastName}`}</span>
    ),
  },
  {
    accessorKey: 'reservationAgent.agency.name',
    header: t('reservations.columns.agencyName'),
  },
  {
    accessorKey: 'reservationAgent.firstname',
    header: t('reservations.columns.agent'),
  },
  {
    accessorKey: 'circuit.countries',
    header: t('reservations.columns.destination'),
    cell: ({ row }) => <span>{row.original.circuit.countries.join(', ')}</span>,
  },
  {
    accessorKey: 'circuit.code',
    header: t('reservations.columns.circuit'),
    cell: ({ row }) => (
      <span>{`C${row.original.circuit.code.toString().padStart(4, '0')}`}</span>
    ),
  },
  {
    accessorKey: 'circuit.durationDays',
    header: t('reservations.columns.duration'),
    cell: ({ row }) =>
      row.original.circuit.durationDays &&
      row.original.circuit.durationNights ? (
        <span>{`${row.original.circuit.durationDays}j / ${row.original.circuit.durationNights}n`}</span>
      ) : row.original.circuit.durationDays ? (
        <span>{`${row.original.circuit.durationDays}j`}</span>
      ) : null,
  },
  {
    accessorKey: 'arrivalDate',
    header: t('reservations.columns.arrivalDate'),
    cell: ({ row }) => (
      <span>{format(new Date(row.original.arrivalDate), 'dd/MM/yyyy')}</span>
    ),
  },
  {
    accessorKey: 'amount',
    header: t('reservations.columns.amount'),
    cell: ({ row }) => {
      const currency =
        row.original.circuit?.currencies[0] !== 'EUR'
          ? (row.original.circuit?.currencies[0] ?? '€')
          : '€';

      return (
        <span>
          {row.original.amount
            ? `${formatPrice(row.original.amount)} ${formatCurrency(currency)}`
            : '-'}
        </span>
      );
    },
  },
  {
    accessorKey: 'reservationRequestSentAt',
    header: t('reservations.columns.requestDate'),
    cell: ({ row }) => (
      <span>
        {row.original.reservationRequestSentAt
          ? format(
              new Date(row.original.reservationRequestSentAt),
              'dd/MM/yyyy',
            )
          : '-'}
      </span>
    ),
  },
  {
    accessorKey: 'confirmedBy',
    header: t('reservations.columns.confirmedBy'),
    cell: ({ row }) => {
      return row.original.confirmedBy ? (
        <span>
          {row.original.confirmedBy.firstname}{' '}
          {row.original.confirmedBy.lastname}
        </span>
      ) : (
        <span>-</span>
      );
    },
  },
  {
    accessorKey: 'status',
    header: t('reservations.columns.status'),
    cell: ({ row }) => (
      <EstimateStatusCell status={row.original.status} isReceiver />
    ),
  },
];
